/**
 * Grading consistency — ONE JOB PER MAIN TASK, once that task has no grading row left.
 *
 * THE PHASE THAT USED TO DECIDE THE UNIT. `reviewGradingConsistencyForTask` compares one task's grades across
 * students, so while it lived inside the grading run the run had to hold a cohort — which is what put a submission
 * SET into `grading`'s dedupe key and cost that type a run-time scope, a dedupe window ending at the claim, and a
 * per-wave store for asks that deduped away. Pulled out, it is a job with a genuinely per-task unit, and `grading`
 * gets to be per (task, submission).
 *
 * WHY THE DEPENDENCY IS A `ready()` GATE AND NOT A FOLLOW-UP. This has to run after the task's grading rows and
 * before the wave is announced. A follow-up fires when the STEP drains, which is after the wave's batch has been
 * closed and its announcement claimed — so a row enqueued there would reopen the step, open a second batch, and
 * produce a second announcement for one upload. Enqueued at grading time and gated on "no `grading` row for my
 * task", it is inside the same batch throughout, and the wave ends when all three of the step's types are done.
 *
 * REFERENCES ARE WHY THIS IS BETTER THAN WHAT IT REPLACES, not merely equivalent. The old pass could only compare
 * the submissions the run happened to cover, so a wave of three was made consistent with itself and thereby pulled
 * away from the 397 it sits among — and a wave of ONE was silently exempt, because `comparableGroups` needs two
 * entries and a lone regrade only ever produced one. This supplies the rest of the cohort's existing grades as
 * read-only references: they establish the standard, the wave's own grades are brought into line with it, and
 * nothing outside the wave can be written. See `buildConsistencyReferences`.
 */

import { logger } from '@adornis/base/logging.js';
import type { ConsistencyReviewInput } from '../../agents/default-grading.js';
import { buildConsistencyReferences, runTaskConsistencyReview } from '../../agents/grading-phases.js';
import { getTaskKey, type NestedExamTask } from '../../agents/tree-generation-utils.js';
import { anyLiveJobForTypesWhere, consistencyItemsOfBatch } from '../job-store.js';
import { gradingStep } from '../steps/grading.step.js';
import { defineJob } from '../registry.js';
import type { GradingScope } from './grading.job.js';

export interface GradingConsistencyPayload {
  /** `getTaskKey` of the main task whose grades are being reconciled. */
  taskKey: string;
  taskNumber: string;
  [key: string]: unknown;
}

export const gradingConsistencyJob = defineJob<GradingConsistencyPayload>({
  type: 'gradingConsistency',

  /**
   * THE SHARED DECLARATION, and it has to be shared rather than omitted: `job-hooks.ts` runs the completion of the
   * TERMINATING row's own type, so a wave whose last row is a consistency row would otherwise find none, close its
   * batch quietly and never announce. `validateStepCompletionWiring` checks at boot that all three types of the step
   * point at this same object.
   */
  onStepComplete: gradingStep,

  /**
   * ONE ROW PER MAIN TASK the wave is grading, dispatched up front by `dispatchGradingWave` rather than enqueued
   * by the grading rows themselves.
   *
   * The tasks are handed in rather than re-enumerated: `dispatchGradingWave` already has them as the payloads
   * `grading` produced, and deriving them a second time from `taskRefs` would be a second implementation of
   * `groupTaskRefsByMainTask` that could drift from the first.
   *
   * A `languageUseGroup`-only wave reconciles nothing — it writes no content grades — which is the same guard the
   * grading handler used to apply before enqueuing this phase.
   */
  batch: {
    units: (_examId, scope) => {
      const { target, consistencyTasks } = scope as GradingScope;
      if ((target ?? 'content') === 'languageUse') return [];
      return (consistencyTasks ?? [])
        .filter(task => task?.taskKey && task?.taskNumber)
        .map(task => ({
          taskKey: task.taskKey,
          taskNumber: task.taskNumber,
          // Carried, not read by the handler: the reconciliation's own scope is the wave's succeeded rows
          // (`consistencyItemsOfBatch`), but the canvas reads these off the live row to decide which boxes
          // are busy — see `LiveGradingJobRow.taskRefs`.
          ...(task.taskRefs?.length ? { taskRefs: task.taskRefs } : {}),
        }));
    },
    /** A caller that waits for the wave has to wait on the row actually doing the work, not on nothing. */
    joinTwin: true,
    waitMs: 10 * 60_000,
  },

  /**
   * Per TASK, and deliberately not the submission lock the grading rows hold: this writes adjustments across many
   * submissions, so it cannot name one of them. Two tasks reconcile concurrently, and nothing here touches
   * `exam.tasks`, so a solution regeneration still runs alongside.
   */
  lockKey: (examId, payload) => `exam:${examId}:consistency:${payload.taskKey}`,

  dedupeKey: (examId, payload) => `gradingConsistency:${examId}:${payload.taskKey}`,

  /**
   * ONLY UNTIL CLAIMED, for the same reason `grading` needs it: this row reads the set it reconciles when it RUNS —
   * the batch's succeeded grading rows — so once it has read that set it cannot stand in for a grade produced
   * afterwards. A grading row arriving late enqueues a consistency row of its own; deduping that onto a running
   * reconciliation would leave the new grade uncompared, which is the one thing this job exists to prevent.
   *
   * A pending one still absorbs everything, which is where the coalescing happens: four hundred grading rows of a
   * task produce one reconciliation, because the first of them is pending for all of it.
   */
  dedupeUntil: 'claimed',

  /**
   * ASYNC, because the gate is a fact about the QUEUE and not about the exam — the one case `ready` was widened for.
   *
   * Scoped to THIS TASK rather than to the step, and that is not an optimisation: the exam-wide predicate would hold
   * every task's reconciliation behind every other task's grading, serialising a wave that has no reason to be
   * serial. A cohort of 400 across 10 tasks would reconcile nothing until all 4000 rows were done.
   */
  ready: async (exam, job) => {
    const root = ((exam.tasks ?? []) as unknown as NestedExamTask[]).find(
      task => getTaskKey(task) === job.payload.taskKey,
    );
    if (!root) return { verdict: 'drop', because: 'gradingConsistency:task-gone' };

    if (await anyLiveJobForTypesWhere(job.examId, ['grading'], { taskKey: job.payload.taskKey })) {
      return { verdict: 'wait', because: 'gradingConsistency:task-still-grading' };
    }
    return 'run';
  },

  /** One model call over one task's entries. Nowhere near a grading run's budget. */
  maxRuntimeMs: 8 * 60_000,
  maxAttempts: 2,

  handler: async (examId, payload, context) => {
    const batchKey = context.job.batchKey;
    if (!batchKey) {
      // Nothing to reconcile against: the targets are the wave's own grading rows, and without a batch there is no
      // wave to read. Only reachable for a row predating batching, so a skip rather than a failure.
      return { skipped: true, note: 'row carries no batch — nothing to reconcile' };
    }

    // THE WAVE'S OWN GRADES, from the rows that succeeded. Asked of the batch rather than of the submissions,
    // because an adjustment needs the write coordinates (`gradingResultFieldPath`, `arrayFilters`) that a grading
    // pass derives while it builds its update — re-deriving them from the stored document would mean reproducing
    // that logic in a second place, where the two could drift.
    //
    // Filtered by task IN THE QUERY: ten tasks each asking for the whole wave's rows would read the whole wave's
    // stored answers ten times.
    const targets = (await consistencyItemsOfBatch(batchKey, payload.taskKey)) as ConsistencyReviewInput[];

    if (targets.length === 0) {
      return { skipped: true, note: 'no graded entries for this task in this wave' };
    }

    const targetSubmissionIds = [...new Set(targets.map(item => item.submissionId))];

    // THE STANDARD THIS EXAM HAS ALREADY BEEN GRADED TO. Empty when the wave covered everyone — the targets are
    // then the comparison set, exactly as the inline pass always worked.
    const references = await buildConsistencyReferences(examId, payload.taskNumber, targetSubmissionIds);

    logger.info(
      {
        examId,
        taskNumber: payload.taskNumber,
        targetSubmissions: targetSubmissionIds.length,
        targetEntries: targets.length,
        referenceEntries: references.length,
      },
      'Reconciling one task’s grades across the wave',
    );

    const { adjustedCount, rubricNote } = await runTaskConsistencyReview(examId, payload.taskNumber, [
      ...targets,
      ...references,
    ]);

    return {
      note: `reconciled ${payload.taskNumber}: ${adjustedCount} adjusted across ${targetSubmissionIds.length} submissions`,
      result: {
        adjustedCount,
        reconciledSubmissionIds: targetSubmissionIds,
        ...(rubricNote ? { rubricNote } : {}),
      },
    };
  },
});
